/**
 * Kundevisnings-vælger til Header: søgbar liste over firmaer brugeren må
 * agere på vegne af. Popover+Command på desktop, Drawer på mobil.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Check, ChevronsUpDown, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { useAuth } from "@/features/auth/AuthContext";
import { useImpersonation } from "@/features/sales/ImpersonationContext";
import { useImpersonatableCompanies } from "@/features/sales/hooks/useImpersonatableCompanies";
import { useIsMobile } from "@/features/sales/hooks/useIsMobile";
import { cn } from "@/lib/utils";

export function CustomerSwitcher() {
  const { isSalesperson } = useAuth();
  const { company, isImpersonating, startImpersonation, stopImpersonation } = useImpersonation();
  const { data: companies, isLoading } = useImpersonatableCompanies();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (!isSalesperson) return null;

  const select = async (companyId: string) => {
    setOpen(false);
    if (companyId === company?.id) return;
    const result = await startImpersonation(companyId);
    if (!result.ok) {
      toast.error(result.error ?? "Kunne ikke starte kundevisning");
      return;
    }
    toast.success("Kundevisning startet");
    navigate("/dashboard");
  };

  const stop = () => {
    stopImpersonation();
    setOpen(false);
    toast.success("Kundevisning afsluttet");
  };

  const list = (
    <Command>
      <CommandInput placeholder="Søg efter firma…" />
      <CommandList>
        <CommandEmpty>{isLoading ? "Henter firmaer…" : "Ingen firmaer fundet."}</CommandEmpty>
        <CommandGroup heading="Kunder">
          {(companies ?? []).map((c) => (
            <CommandItem
              key={c.id}
              value={`${c.name} ${c.cvr ?? ""}`}
              onSelect={() => void select(c.id)}
              className="min-h-11"
            >
              <Check className={cn("mr-2 h-4 w-4", company?.id === c.id ? "opacity-100" : "opacity-0")} />
              <span className="truncate">{c.name}</span>
              {c.status !== "active" && (
                <span className="ml-auto text-xs text-muted-foreground">({c.status})</span>
              )}
            </CommandItem>
          ))}
        </CommandGroup>
        {isImpersonating && (
          <CommandGroup>
            <CommandItem onSelect={stop} className="min-h-11 text-destructive">
              <X className="mr-2 h-4 w-4" />
              Afslut kundevisning
            </CommandItem>
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  );

  const trigger = (
    <Button
      variant={isImpersonating ? "secondary" : "outline"}
      size="sm"
      className="min-h-11 max-w-[220px] justify-between gap-2"
      aria-label="Vælg kundevisning"
    >
      <Building2 className="h-4 w-4 shrink-0" />
      <span className="truncate">{isImpersonating ? company?.name : "Kundevisning"}</span>
      <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
    </Button>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent>
          <DrawerHeader className="pb-0">
            <DrawerTitle>Kundevisning</DrawerTitle>
          </DrawerHeader>
          <div className="p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">{list}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="end">
        {list}
      </PopoverContent>
    </Popover>
  );
}
